import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { HugeiconsIcon } from '@hugeicons/react'
import { Add01Icon, CheckmarkCircleIcon } from '@hugeicons/core-free-icons'
import { installCommand, isCommandInstalled, uninstallCommand } from '@/atoms/slash-commands'
import { useCurrentPubkey } from '@/atoms/session'
import {
  describeScopes,
  isCommandInstallableForUser,
  parseCommandDefinition,
  TCommandArg
} from '../../lib/slash-command-definition'

const ARG_TYPE_LABELS: Record<string, string> = {
  pubkey: 'pubkey',
  event: 'event',
  address: 'address',
  relay: 'relay',
  number: 'number',
  bool: 'bool',
  enum: 'enum',
  word: 'word',
  text: 'text'
}

export default function SlashCommandDefinition({
  event,
  className
}: {
  event: Event
  className?: string
}) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const definition = useMemo(() => parseCommandDefinition(event.tags), [event])
  const scopeDescriptions = useMemo(() => describeScopes(definition.scopes), [definition.scopes])
  const ignoreDescriptions = useMemo(() => describeScopes(definition.ignores), [definition.ignores])
  const canInstall =
    // the command must not be restricted to other authors
    !!pubkey &&
    isCommandInstallableForUser(definition, pubkey) &&
    // and its kind: scopes (if any) must allow notes or comments (kind 1 or 1111)
    definition.scopes
      .filter((scope) => scope.startsWith('kind:'))
      .every((scope) => {
        const kind = Number(scope.slice('kind:'.length))
        return kind === 1 || kind === 1111
      })
  const [installed, setInstalled] = useState(() => isCommandInstalled(event.pubkey, definition.d))

  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-sm text-muted-foreground font-medium">{t('Command')}</div>
      <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
        {definition.command && (
          <span className="font-mono font-semibold text-lg text-primary break-all">
            /{definition.command}
          </span>
        )}
        {definition.title && (
          <span className="text-base font-medium break-words">{definition.title}</span>
        )}
        {(installed || canInstall) && (
          <Button
            variant={installed ? 'outline' : 'secondary'}
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => {
              if (installed) {
                uninstallCommand(event.pubkey, definition.d)
                setInstalled(false)
              } else {
                installCommand(event.pubkey, event.tags)
                setInstalled(true)
              }
            }}
          >
            <HugeiconsIcon icon={installed ? CheckmarkCircleIcon : Add01Icon} />
            {installed ? t('Installed') : t('Install')}
          </Button>
        )}
      </div>
      {definition.description && (
        <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
          {definition.description}
        </p>
      )}
      {definition.args.length > 0 && (
        <div className="space-y-1">
          {definition.args.map((arg, i) => (
            <CommandArgRow key={`${arg.name}:${i}`} arg={arg} />
          ))}
        </div>
      )}
      {(scopeDescriptions.length > 0 || ignoreDescriptions.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {scopeDescriptions.map((scope, i) => (
            <Badge key={`s:${i}`} variant="secondary" className="font-mono">
              {scope}
            </Badge>
          ))}
          {ignoreDescriptions.map((scope, i) => (
            <Badge key={`ignore:${i}`} variant="outline" className="font-mono line-through">
              {scope}
            </Badge>
          ))}
        </div>
      )}
      {definition.d && (
        <div className="truncate font-mono text-xs text-muted-foreground">#d: {definition.d}</div>
      )}
      {event.content && <p className="whitespace-pre-wrap break-words text-sm">{event.content}</p>}
    </div>
  )
}

function CommandArgRow({ arg }: { arg: TCommandArg }) {
  const typeLabel = ARG_TYPE_LABELS[arg.type] ?? arg.type

  return (
    <div className="flex items-baseline gap-2 text-sm min-w-0">
      <span className="font-mono font-medium shrink-0">{arg.name}</span>
      <span className="text-muted-foreground text-xs font-mono shrink-0">
        {typeLabel}
        {arg.required ? '' : ` (${'optional'})`}
      </span>
      {arg.label && <span className="text-muted-foreground truncate">{arg.label}</span>}
      {arg.choices.length > 0 && (
        <span className="text-muted-foreground text-xs truncate">[{arg.choices.join(', ')}]</span>
      )}
    </div>
  )
}
